import { useEffect, useState } from 'react';
import { Shield, RefreshCw, XCircle, CheckCircle, Info } from 'lucide-react';
import api from '../../services/api';
import StatusBadge from '../common/StatusBadge';
import { formatDate } from '../../utils/formatDate';
import { useSettingsStore } from '../../stores/settingsStore';
import { isSystemAcl, getAclDescription } from '../../utils/aclUtils';

interface ACLStatus {
  edgeId: string;
  enabled: boolean;
  totalAcls: number;
  totalRules: number;
  acls: {
    aclName: string;
    rulesCount: number;
    packetsMatched: number;
    bytesMatched: number;
    packetsPermit: number;
    packetsDeny: number;
    interfaces: {
      ingress: string[];
      egress: string[];
    };
  }[];
  lastUpdated: string | null;
}

interface ACLStatusCardProps {
  edgeGuid: string;
  autoRefresh?: boolean;
  refreshInterval?: number;
  vppAclStatus?: any;
}

const ACLStatusCard = ({
  edgeGuid,
  autoRefresh = true,
  refreshInterval = 30000,
  vppAclStatus,
}: ACLStatusCardProps) => {
  const { getDateFormatSettings } = useSettingsStore();
  const dateSettings = getDateFormatSettings();
  const [status, setStatus] = useState<ACLStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = async () => {
    try {
      const response = await api.get(`/api/v2/edges/${edgeGuid}/acl/stats`);
      if (response.data?.data) {
        setStatus(response.data.data);
      } else {
        setStatus(response.data);
      }
      setError(null);
    } catch (err: any) {
      setError('Failed to fetch ACL status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    if (autoRefresh) {
      const interval = setInterval(fetchStatus, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [edgeGuid, autoRefresh, refreshInterval]);

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-600">Loading ACL status...</span>
        </div>
      </div>
    );
  }

  if (error || !status) {
    return (
      <div className="card">
        <div className="flex items-center text-yellow-600">
          <Shield className="w-5 h-5 mr-2" />
          <span>No ACL data available. Configure ACLs to see statistics.</span>
        </div>
      </div>
    );
  }

  // Compute totals — prefer VPP status for rule counts when available
  const vppRules = vppAclStatus?.rules || [];
  const vppTotalRules = vppRules.reduce(
    (sum: number, acl: any) => sum + (acl.entries?.length || acl.rule_count || 0),
    0
  );
  const vppTotalAcls = vppRules.length;

  const displayTotalRules = vppTotalRules > 0 ? vppTotalRules : status.totalRules;
  const displayTotalAcls = vppTotalAcls > 0 ? vppTotalAcls : status.totalAcls;

  // Count user vs system ACLs
  const allAclNames = vppTotalAcls > 0
    ? vppRules.map((r: any) => r.name)
    : status.acls.map(a => a.aclName);
  const userAclCount = allAclNames.filter((n: string) => !isSystemAcl(n)).length;
  const systemAclCount = allAclNames.filter((n: string) => isSystemAcl(n)).length;

  // Calculate totals from API metrics
  const totalPacketsPermit = status.acls.reduce((sum, acl) => sum + acl.packetsPermit, 0);
  const totalPacketsDeny = status.acls.reduce((sum, acl) => sum + acl.packetsDeny, 0);
  const allPacketsZero = totalPacketsPermit === 0 && totalPacketsDeny === 0;

  // Build unified ACL detail list: merge API metrics with VPP status
  const aclDetails = vppTotalAcls > 0
    ? vppRules.map((vppAcl: any) => {
        const apiAcl = status.acls.find(a => a.aclName === vppAcl.name);
        const entries = vppAcl.entries || [];
        const ruleCount = entries.length || vppAcl.rule_count || apiAcl?.rulesCount || 0;
        // VSNP-1009: classify each rule's action, distinguishing plain permit
        // (stateless, VPP action 1) from permit+reflect (stateful, action 2).
        // Entries may carry the action as a number (0/1/2) or a string
        // ('permit' / 'deny' / 'permit_reflect' / 'reflect'). Check reflect
        // first so 'permit_reflect' isn't swallowed by the 'permit' match.
        const classifyAction = (e: any): 'deny' | 'permit' | 'reflect' => {
          const act = e.action ?? '';
          if (typeof act === 'string') {
            const s = act.toLowerCase();
            if (s.includes('reflect') || s.includes('stateful')) return 'reflect';
            if (s.includes('deny')) return 'deny';
            if (s.includes('permit')) return 'permit';
            return 'deny';
          }
          return act === 2 ? 'reflect' : act === 1 ? 'permit' : 'deny';
        };
        const permitRules = entries.filter((e: any) => classifyAction(e) === 'permit').length;
        const reflectRules = entries.filter((e: any) => classifyAction(e) === 'reflect').length;
        const denyRules = entries.filter((e: any) => classifyAction(e) === 'deny').length;
        return {
          name: vppAcl.name,
          ruleCount,
          permitRules,
          reflectRules,
          denyRules,
          packetsPermit: apiAcl?.packetsPermit || 0,
          packetsDeny: apiAcl?.packetsDeny || 0,
          bytesMatched: apiAcl?.bytesMatched || 0,
          enabled: vppAcl.enabled ?? false,
        };
      })
    : status.acls.map(acl => ({
        name: acl.aclName,
        ruleCount: acl.rulesCount,
        permitRules: 0,
        reflectRules: 0,
        denyRules: 0,
        packetsPermit: acl.packetsPermit,
        packetsDeny: acl.packetsDeny,
        bytesMatched: acl.bytesMatched,
        enabled: true,
      }));

  const totalBound = aclDetails.filter((a: any) => a.enabled).length;

  return (
    <div className="space-y-4">
      {/* ACL Plugin Status */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Shield className="w-6 h-6 text-primary-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">VPP ACL Status</h3>
          </div>
          <StatusBadge status={status.enabled ? 'Online' : 'Offline'} />
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div>
            <p className="text-sm text-gray-600">Total ACLs</p>
            <p className="text-2xl font-bold text-blue-600">{displayTotalAcls}</p>
            {(userAclCount > 0 || systemAclCount > 0) && (
              <p className="text-xs text-gray-500 mt-0.5">
                {userAclCount} user, {systemAclCount} system
              </p>
            )}
          </div>
          <div>
            <p className="text-sm text-gray-600">Total Rules</p>
            <p className="text-2xl font-bold text-purple-600">{displayTotalRules}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Packets Permitted</p>
            <p className="text-2xl font-bold text-green-600">{totalPacketsPermit.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Packets Denied</p>
            <p className="text-2xl font-bold text-red-600">{totalPacketsDeny.toLocaleString()}</p>
          </div>
        </div>

        {/* Info banner when counters are zero */}
        {allPacketsZero && status.enabled && (
          <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700 mb-4">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>ACL hit counters pending — data will populate after the next agent update with hitcount collection.</span>
          </div>
        )}

        {/* Active ACL Bindings */}
        <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
          <span className="text-sm text-gray-600">ACLs Bound to Interfaces</span>
          <span className="text-sm font-medium">{totalBound} / {displayTotalAcls}</span>
        </div>

        {/* Last Updated */}
        {status.lastUpdated && (
          <div className="mt-4 text-xs text-gray-500">
            Last updated: {formatDate(status.lastUpdated, dateSettings)}
          </div>
        )}
      </div>

      {/* Per-ACL Stats */}
      {aclDetails.length > 0 && (
        <div className="card">
          <h4 className="text-md font-semibold text-gray-900 mb-4">ACL Details</h4>
          <div className="space-y-3">
            {aclDetails.map((acl: any) => {
              const system = isSystemAcl(acl.name);
              const description = getAclDescription(acl.name);

              return (
                <div key={acl.name} className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{acl.name}</span>
                      {system ? (
                        <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-600">System</span>
                      ) : (
                        <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-700">User</span>
                      )}
                      {description && (
                        <span className="text-xs text-gray-400" title={description}>{description}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                      <span>{acl.ruleCount} rules</span>
                      {(acl.permitRules > 0 || acl.reflectRules > 0 || acl.denyRules > 0) ? (
                        // VSNP-1009: surface stateful (permit+reflect) rules
                        // separately from plain permit so sys-wan-guard reads
                        // "(1 permit (stateful))" rather than "(1 permit)".
                        <span className="text-xs">
                          ({[
                            acl.permitRules > 0 && `${acl.permitRules} permit`,
                            acl.reflectRules > 0 && `${acl.reflectRules} permit (stateful)`,
                            acl.denyRules > 0 && `${acl.denyRules} deny`,
                          ].filter(Boolean).join(', ')})
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
                      <span className="text-gray-600">{acl.packetsPermit.toLocaleString()} pkts permit</span>
                    </div>
                    <div className="flex items-center">
                      <XCircle className="w-4 h-4 text-red-500 mr-1" />
                      <span className="text-gray-600">{acl.packetsDeny.toLocaleString()} pkts deny</span>
                    </div>
                    <div>
                      <span className={acl.bytesMatched === 0 ? 'text-gray-400' : 'text-gray-600'}>
                        {acl.bytesMatched > 0 ? acl.bytesMatched.toLocaleString() + ' bytes' : 'bytes N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ACLStatusCard;
